<script>
  // M3 multi-select with searchable popover. Trigger looks like an outlined
  // text field; clicking opens a docked panel with a search box + scrollable
  // checklist of options. Selected values surface as small chips inside the
  // trigger, with a "+N more" overflow when there isn't room.
  //
  // Designed for filter lists that may have hundreds of entries (winston log
  // hostnames, log_types, versions). Keeps the page short by replacing inline
  // chip walls with a single 56px trigger.
  import { onMount, tick } from 'svelte';
  import { tooltip } from '../lib/tooltip.js';

  let {
    label,
    values  = $bindable([]),  // selected values (always an array, even in single mode)
    options = [],             // string[] | { value, label }[]
    placeholder = '',         // shown when nothing is selected
    single  = false,          // when true: picking a value replaces the array and closes
    onsearch = null,          // called with the query when the search box changes,
                              // for lists too big to ship to the browser up front
  } = $props();

  let open = $state(false);
  let trigger = $state(null);
  let panel = $state(null);
  let panelStyle = $state('');
  let search = $state('');
  let searchInput = $state(null);

  // Focusing the search box saves a click on a desktop, but on a phone it pops
  // the on-screen keyboard straight over the list you just opened. Width
  // decides, not touch support -- plenty of desktops have a touch screen.
  // 1024px clears the widest phone in landscape (~930px).
  const FOCUS_SEARCH_MIN_WIDTH = 1024;

  // Normalize options to { value, label }.
  const normalized = $derived(
    (options || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
  );
  // A chip is only meaningful for the panel rows; the trigger keeps plain text.

  // Tooltip text: full list of selected labels so hovering a truncated
  // trigger ("a, b +5") reveals everything.
  // What the trigger and the tooltip show for the current selection: the label,
  // not the raw value. A customer or a user reads back as a bare id otherwise,
  // which says nothing about what was picked. Falls back to the value when a
  // selection isn't in the current option list -- the phone picker searches
  // server-side, so its list changes underneath the selection.
  const selectedLabels = $derived.by(() => {
    const byValue = new Map(normalized.map((o) => [o.value, o.label]));
    return values.map((v) => byValue.get(v) ?? String(v));
  });
  const tooltipText = $derived.by(() => {
    const labels = selectedLabels;
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    return `${labels.length} selected:\n• ${labels.join('\n• ')}`;
  });
  // Cap the rendered list so a list with hundreds of options doesn't freeze
  // the renderer when the panel opens. The user narrows with the search box.
  const VISIBLE_LIMIT = 200;

  // Rank matches instead of taking them in source order. With a few thousand
  // options ("3" matches half the customer directory) an unranked list shows an
  // arbitrary slice, so the customer whose id IS 3 never appears. Search the id
  // first, then the name:
  //
  //   0  id is exactly the query      3        -> customer #3
  //   1  id starts with the query     30, 312
  //   2  name starts with the query   "3M Corp"
  //   3  id contains the query        1234
  //   4  name contains the query      "Studio 3"
  //
  // Ties keep their original order, so an already-sorted list stays sorted
  // within each band.
  const NO_MATCH = 99;
  function rankOption(o, q) {
    const value = String(o.value).toLowerCase();
    const label = String(o.label).toLowerCase();
    if (value === q) return 0;
    if (value.startsWith(q)) return 1;
    if (label.startsWith(q)) return 2;
    if (value.includes(q)) return 3;
    if (label.includes(q)) return 4;
    return NO_MATCH;
  }
  const filtered = $derived.by(() => {
    const q = search.trim().toLowerCase();
    if (!q) return normalized;
    const hits = [];
    normalized.forEach((o, i) => {
      const rank = rankOption(o, q);
      if (rank !== NO_MATCH) hits.push({ o, rank, i });
    });
    hits.sort((a, b) => a.rank - b.rank || a.i - b.i);
    return hits.map((h) => h.o);
  });
  const truncated = $derived(filtered.length > VISIBLE_LIMIT);
  const visible = $derived(truncated ? filtered.slice(0, VISIBLE_LIMIT) : filtered);

  function isSelected(v) { return values.includes(v); }

  function toggle(v) {
    if (single) {
      // Replace + close. Re-picking the current value clears it instead.
      values = (values.length === 1 && values[0] === v) ? [] : [v];
      closePanel();
      return;
    }
    const i = values.indexOf(v);
    if (i >= 0) values.splice(i, 1); else values.push(v);
    values = [...values]; // re-trigger reactive consumers
  }

  function clearAll() { values = []; }

  async function openPanel() {
    if (open) { closePanel(); return; }
    open = true;
    search = '';
    await tick();
    position();
    if (window.innerWidth >= FOCUS_SEARCH_MIN_WIDTH) searchInput?.focus();
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
  }
  function closePanel() {
    open = false;
    document.removeEventListener('mousedown', onDocMouseDown);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', position);
    window.removeEventListener('scroll', position, true);
  }

  function position() {
    if (!trigger || !panel) return;
    const t = trigger.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth, vh = window.innerHeight;
    // Fix the width here rather than letting the content set it. With only a
    // min-width the longest option ("271 NY Holding LLC DBA /Just Klean LLC
    // (#1270)") stretched the panel well past the width `left` was calculated
    // for, so it hung off the right edge of the screen.
    const pw = Math.min(Math.max(t.width, 280), Math.max(240, vw - margin * 2));
    let left = t.left;
    if (left + pw + margin > vw) left = vw - pw - margin;
    if (left < margin) left = margin;
    let top = t.bottom + 4;
    const ph = Math.min(360, vh - top - margin);
    if (ph < 200 && t.top - 4 - 320 > margin) {
      top = t.top - 4 - 320;
      panelStyle = `top:${top}px; left:${left}px; width:${pw}px; max-height:320px;`;
      return;
    }
    panelStyle = `top:${top}px; left:${left}px; width:${pw}px; max-height:${ph}px;`;
  }

  function onDocMouseDown(e) {
    if (trigger?.contains(e.target) || panel?.contains(e.target)) return;
    closePanel();
  }
  function onKey(e) { if (e.key === 'Escape') closePanel(); }

  // Let the owner fetch matches it hasn't shipped to the browser. Fires on the
  // reset to '' when the panel opens too, which is how the owner knows to go
  // back to its default list.
  $effect(() => {
    const q = search;
    if (!open) return;
    onsearch?.(q);
  });

  onMount(() => () => closePanel());
</script>

<div class="md-ms">
  <button
    bind:this={trigger}
    type="button"
    class="md-ms-trigger"
    class:open
    onclick={openPanel}
    aria-haspopup="listbox"
    aria-expanded={open}
    use:tooltip={tooltipText}
  >
    <span class="md-ms-text" class:placeholder={values.length === 0}>
      {#if values.length === 0}
        {placeholder || ' '}
      {:else if values.length <= 2}
        {selectedLabels.join(', ')}
      {:else}
        {selectedLabels.slice(0, 2).join(', ')} <span class="md-ms-more">+{values.length - 2}</span>
      {/if}
    </span>
    <span class="md-ms-label">{label}</span>
    <span class="material-symbols-outlined md-ms-chevron" class:open>arrow_drop_down</span>
  </button>

  {#if open}
    <div bind:this={panel} class="md-ms-panel" style={panelStyle} role="listbox" aria-multiselectable="true">
      <div class="md-ms-searchwrap">
        <span class="material-symbols-outlined md-ms-searchicon">search</span>
        <input
          class="md-ms-search"
          type="text"
          placeholder="Search {label.toLowerCase()}…"
          bind:value={search}
          bind:this={searchInput}
        />
        {#if values.length > 0}
          <button type="button" class="md-ms-clear" onclick={clearAll} title="Clear all">
            <span class="material-symbols-outlined">backspace</span>
          </button>
        {/if}
      </div>

      <div class="md-ms-list">
        {#if filtered.length === 0}
          <p class="md-ms-empty">No matches</p>
        {/if}
        {#each visible as opt (opt.value)}
          <button
            type="button"
            role="option"
            aria-selected={isSelected(opt.value)}
            class="md-ms-item"
            class:selected={isSelected(opt.value)}
            onclick={() => toggle(opt.value)}
          >
            <span class="md-ms-check">
              {#if isSelected(opt.value)}
                <span class="material-symbols-outlined">check_box</span>
              {:else}
                <span class="material-symbols-outlined">check_box_outline_blank</span>
              {/if}
            </span>
            <span class="md-ms-itemlabel">
              {#if opt.chipClass}
                <span class="md-ms-chip {opt.chipClass}">{opt.label}</span>
              {:else}
                {opt.label}
              {/if}
            </span>
          </button>
        {/each}
        {#if truncated}
          <p class="md-ms-empty">Showing first {VISIBLE_LIMIT} of {filtered.length}. Type to narrow.</p>
        {/if}
      </div>

      {#if values.length > 0}
        <div class="md-ms-foot">
          <span class="md-ms-count">{values.length} selected</span>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .md-ms { position: relative; display: block; width: 100%; }

  .md-ms-trigger {
    width: 100%;
    height: 56px;
    padding: 0 44px 0 16px;
    background: rgb(var(--md-tf-surface, var(--md-sys-color-surface)));
    border: 1px solid rgb(var(--md-sys-color-outline));
    border-radius: 4px;
    color: rgb(var(--md-sys-color-on-surface));
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    cursor: pointer;
    position: relative;
    font-family: inherit;
    transition: border-color 150ms, border-width 150ms;
  }
  @media (max-width: 768px) {
    .md-ms-trigger { height: 48px; font-size: 14px; }
    .md-ms-text { line-height: 48px; }
  }
  .md-ms-trigger:hover { border-color: rgb(var(--md-sys-color-on-surface)); }
  .md-ms-trigger:focus, .md-ms-trigger.open {
    outline: none;
    border-color: rgb(var(--md-sys-color-primary));
    border-width: 2px;
    padding: 0 43px 0 15px;
  }
  .md-ms-text { display: block; line-height: 56px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .md-ms-text.placeholder { color: transparent; }
  .md-ms-more { color: rgb(var(--md-sys-color-on-surface-variant)); }

  .md-ms-label {
    position: absolute;
    left: 12px;
    top: 0;
    transform: translateY(-50%);
    padding: 0 4px;
    background: rgb(var(--md-tf-surface, var(--md-sys-color-surface)));
    color: rgb(var(--md-sys-color-on-surface-variant));
    font-size: 12px;
    line-height: 1;
    pointer-events: none;
  }
  .md-ms-trigger:focus ~ .md-ms-label,
  .md-ms-trigger.open  ~ .md-ms-label { color: rgb(var(--md-sys-color-primary)); }

  .md-ms-chevron {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgb(var(--md-sys-color-on-surface-variant));
    pointer-events: none;
    transition: transform 200ms;
  }
  .md-ms-chevron.open { transform: translateY(-50%) rotate(180deg); }

  .md-ms-panel {
    position: fixed;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    background: rgb(var(--md-sys-color-surface-container-high));
    color: rgb(var(--md-sys-color-on-surface));
    border-radius: 12px;
    box-shadow:
      0 1px 3px 0 rgba(0,0,0,0.30),
      0 4px 8px 3px rgba(0,0,0,0.15);
    overflow: hidden;
  }

  .md-ms-searchwrap {
    position: relative;
    padding: 8px;
    border-bottom: 1px solid rgb(var(--md-sys-color-outline-variant));
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .md-ms-searchicon {
    position: absolute;
    left: 18px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    pointer-events: none;
    font-size: 20px;
  }
  .md-ms-search {
    width: 100%;
    height: 36px;
    padding: 0 32px 0 36px;
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    border-radius: 18px;
    background: rgb(var(--md-sys-color-surface));
    color: rgb(var(--md-sys-color-on-surface));
    font-family: inherit;
    font-size: 14px;
    outline: none;
  }
  .md-ms-search:focus { border-color: rgb(var(--md-sys-color-primary)); }
  .md-ms-clear {
    position: absolute;
    right: 14px;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    cursor: pointer;
  }
  .md-ms-clear:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .md-ms-clear .material-symbols-outlined { font-size: 18px; }

  .md-ms-list {
    flex: 1;
    overflow-y: auto;
    padding: 4px 0;
  }
  .md-ms-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 0 12px;
    height: 40px;
    background: transparent;
    color: inherit;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    font-size: 14px;
  }
  .md-ms-item:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .md-ms-item.selected { background: rgb(var(--md-sys-color-primary) / 0.12); }
  .md-ms-check { display: inline-flex; color: rgb(var(--md-sys-color-on-surface-variant)); }
  .md-ms-item.selected .md-ms-check { color: rgb(var(--md-sys-color-primary)); }
  .md-ms-check .material-symbols-outlined { font-size: 20px; }
  .md-ms-itemlabel { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  /* An option may carry `chipClass` so it reads the same here as it does in the
     list it filters -- the level pills are the reason this exists. The colour
     classes come from the consumer, so they stay in one place. */
  .md-ms-chip {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
  }
  .md-ms-empty { padding: 16px; text-align: center; color: rgb(var(--md-sys-color-on-surface-variant)); font-size: 14px; }

  .md-ms-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    border-top: 1px solid rgb(var(--md-sys-color-outline-variant));
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .md-ms-done {
    height: 32px;
    padding: 0 16px;
    border-radius: 16px;
    background: transparent;
    color: rgb(var(--md-sys-color-primary));
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;
  }
  .md-ms-done:hover { background: rgb(var(--md-sys-color-primary) / 0.08); }
</style>
